import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import api from './utils/axios';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './components/Login';
import Settings from './components/Settings';
import AdminSettings from './components/AdminSettings';
import QuotationWizard from './components/QuotationWizard';
import AanvragenDashboard from './components/AanvragenDashboard';
import LandingPage from './components/LandingPage';
import SavedQuotations from './components/SavedQuotations';
import TrialBanner from './components/TrialBanner';
import DemoLayout from './components/DemoLayout';
import BlogList from './components/BlogList';
import BlogPost from './components/BlogPost';
import PageView from './components/PageView';
import CmsAdmin from './components/CmsAdmin';
import './App.css';

// Scroll to top on route changes
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

class AppErrorBoundary extends React.Component {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error('App error:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="App" style={{ padding: 24, maxWidth: 480, margin: '40px auto', textAlign: 'center' }}>
          <p style={{ marginBottom: 16 }}>Er is iets misgegaan in de app.</p>
          <p style={{ fontSize: 14, color: '#666', marginBottom: 24 }}>Probeer de pagina te vernieuwen of log opnieuw in.</p>
          {this.props.onLogout && (
            <button type="button" className="btn btn-primary" onClick={this.props.onLogout}>
              Uitloggen
            </button>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

// Binnen Router (Route element) zodat useLocation werkt
function ProtectedLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, subscribers, subscribersLoaded, selectedSubscriberId, setSelectedSubscriberId, loadSubscribers, isLicenseActive, trialEndsAt, isTrialExpired, checkAuth } = useAuth();
  const [syncingCompanies, setSyncingCompanies] = useState(false);
  const [syncingCustomers, setSyncingCustomers] = useState(false);
  const [wizardKey, setWizardKey] = useState(0);
  const [handleidingLoading, setHandleidingLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  const hasNoCompanies = subscribersLoaded && (!subscribers || subscribers.length === 0) && !selectedSubscriberId;
  const isSettingsPage = location.pathname === '/instellingen' || location.pathname === '/instellingen/admin' || location.pathname.startsWith('/instellingen/admin/');
  const showNoCompaniesSetup = hasNoCompanies && !isSettingsPage;

  // Geen volledige blokkade meer: wizard is toegankelijk (stap 5+ wordt in QuotationWizard geblokkeerd)
  const showSetupBlock = showNoCompaniesSetup;

  const handleSyncCompanies = async () => {
    if (!window.confirm('Bedrijven uit Odoo ophalen en toevoegen aan Supabase?')) return;
    try {
      setSyncingCompanies(true);
      const res = await api.post('/api/subscribers/sync-from-odoo');
      alert(res.data?.message || 'Bedrijven gesynchroniseerd');
      await loadSubscribers();
    } catch (err) {
      alert(err.response?.data?.message || err.response?.data?.error || 'Sync mislukt. Controleer Odoo-gegevens in Instellingen.');
    } finally {
      setSyncingCompanies(false);
    }
  };

  const handleSyncCustomers = async () => {
    if (!selectedSubscriberId) {
      alert('Selecteer eerst een bedrijf');
      return;
    }
    if (!window.confirm('Klanten uit Odoo ophalen en synchroniseren naar Supabase?')) return;
    try {
      setSyncingCustomers(true);
      const res = await api.post('/api/customers/sync-from-odoo');
      alert(res.data?.message || 'Klanten gesynchroniseerd');
    } catch (err) {
      alert(err.response?.data?.message || err.response?.data?.error || 'Sync mislukt. Controleer Odoo-gegevens.');
    } finally {
      setSyncingCustomers(false);
    }
  };

  const handleDownloadHandleiding = async () => {
    setHandleidingLoading(true);
    try {
      const res = await api.get('/api/handleiding', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.setAttribute('download', 'Offerterunner_handleiding.pdf');
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (err.response?.status === 404) {
        alert('Handleiding is niet beschikbaar.');
      } else {
        alert(err.response?.data?.error || 'Download mislukt.');
      }
    } finally {
      setHandleidingLoading(false);
    }
  };

  return (
    <AppErrorBoundary onLogout={logout}>
      <div className="App">
        {typeof sessionStorage !== 'undefined' && sessionStorage.getItem('impersonating') === '1' && (
          <div
            className="impersonation-banner"
            role="banner"
            style={{
              background: 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)',
              color: '#fff',
              padding: '10px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
              flexWrap: 'wrap',
              fontSize: 14,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
            }}
          >
            <span>Je bekijkt de app als <strong>{user?.email}</strong></span>
            <button
              type="button"
              className="btn btn-primary"
              style={{ background: '#fff', color: '#b45309', border: 'none', padding: '6px 14px', fontWeight: 600 }}
              onClick={() => {
                const adminToken = sessionStorage.getItem('adminTokenBeforeImpersonate');
                sessionStorage.removeItem('impersonating');
                sessionStorage.removeItem('adminTokenBeforeImpersonate');
                if (adminToken) localStorage.setItem('authToken', adminToken);
                window.location.reload();
              }}
            >
              ← Terug naar beheeromgeving
            </button>
          </div>
        )}
        {!isLicenseActive && user?.role !== 'admin' && (
          <TrialBanner
            trialEndsAt={trialEndsAt}
            licenseActive={isLicenseActive}
            onLicenseActivated={() => checkAuth()}
          />
        )}
        <div className="app-header">
          <div className="header-content">
            <img src="/logo-offerterunner.png" alt="OfferteRunner" className="header-logo" />
            <button
              type="button"
              className="hamburger-btn"
              onClick={() => setMobileMenuOpen(o => !o)}
              aria-label="Menu"
              aria-expanded={mobileMenuOpen}
            >
              <span className={`hamburger-icon${mobileMenuOpen ? ' open' : ''}`} />
            </button>
            <div className={`header-right${mobileMenuOpen ? ' mobile-open' : ''}`}>
              <div className="header-nav">
                <button
                  type="button"
                  className="btn btn-primary btn-small nav-btn"
                  onClick={() => {
                    setWizardKey(k => k + 1);
                    navigate('/', { replace: true, state: {} });
                    setMobileMenuOpen(false);
                  }}
                >
                  <span className="nav-btn-icon" aria-hidden>➕</span>
                  Nieuwe offerte
                </button>
                <Link className="btn btn-secondary btn-small nav-btn" to="/offertes" onClick={() => setMobileMenuOpen(false)}>
                  <span className="nav-btn-icon" aria-hidden>📄</span>
                  Offertes
                </Link>
                <Link className="btn btn-secondary btn-small nav-btn" to="/aanvragen" onClick={() => setMobileMenuOpen(false)}>
                  <span className="nav-btn-icon" aria-hidden>📥</span>
                  Aanvragen
                </Link>
                <Link className="btn btn-secondary btn-small nav-btn" to="/instellingen" onClick={() => setMobileMenuOpen(false)}>
                  <span className="nav-btn-icon" aria-hidden>⚙️</span>
                  Instellingen
                </Link>
                <button
                  type="button"
                  className="btn btn-secondary btn-small nav-btn"
                  onClick={() => { handleDownloadHandleiding(); setMobileMenuOpen(false); }}
                  disabled={handleidingLoading}
                  title="Download de OfferteRunner handleiding (PDF)"
                >
                  <span className="nav-btn-icon" aria-hidden>📕</span>
                  {handleidingLoading ? 'Bezig...' : 'Download handleiding'}
                </button>
                {user?.role === 'admin' && (
                  <Link className="btn btn-secondary btn-small nav-btn" to="/instellingen/admin" onClick={() => setMobileMenuOpen(false)}>
                    <span className="nav-btn-icon" aria-hidden>🔐</span>
                    Admin
                  </Link>
                )}
                <button
                  type="button"
                  className="btn btn-secondary btn-small nav-btn"
                  onClick={() => { logout(); setMobileMenuOpen(false); }}
                >
                  <span className="nav-btn-icon" aria-hidden>🚪</span>
                  Uitloggen
                </button>
              </div>
              <div className="header-toolbar">
                <button
                  className="btn btn-secondary btn-small"
                  onClick={handleSyncCompanies}
                  disabled={syncingCompanies}
                  title="Bedrijven uit Odoo ophalen"
                >
                  {syncingCompanies ? '⏳' : '🔄'} Sync bedrijven
                </button>
                {selectedSubscriberId && (
                  <button
                    className="btn btn-secondary btn-small"
                    onClick={handleSyncCustomers}
                    disabled={syncingCustomers}
                    title="Klanten uit Odoo ophalen voor geselecteerd bedrijf"
                  >
                    {syncingCustomers ? '⏳' : '👥'} Sync klanten
                  </button>
                )}
                <label className="header-bedrijf">
                  <span className="header-bedrijf-label">Bedrijf:</span>
                  <select
                    className="header-bedrijf-select"
                    value={selectedSubscriberId}
                    onChange={(e) => setSelectedSubscriberId(e.target.value)}
                  >
                    <option value="">— Selecteer bedrijf —</option>
                    {(subscribers || []).map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.company_name || s.companyName || 'Onbenoemd'}
                      </option>
                    ))}
                    {(!subscribers || subscribers.length === 0) && (
                      <option value="" disabled>Geen bedrijven</option>
                    )}
                  </select>
                </label>
              </div>
            </div>
          </div>
        </div>

        {showNoCompaniesSetup && (
          <div className="app-setup-message" style={{ padding: 32, maxWidth: 520, margin: '40px auto', textAlign: 'center' }}>
            <h2 style={{ marginBottom: 12 }}>Nog geen bedrijven</h2>
            <p style={{ color: '#555', marginBottom: 16 }}>
              Koppel je Odoo-omgeving om je bedrijven automatisch op te halen.
            </p>
            <p style={{ color: '#555', marginBottom: 16 }}>
              Ga naar <strong>Instellingen</strong>, vul je Odoo-gegevens in en klik op &quot;Sync bedrijven uit Odoo&quot;.
            </p>
            <p style={{ color: '#555', marginBottom: 24, fontSize: 14 }}>
              Wil je dat wij de instellingen voor je doen? Uiteraard kan dat. Stuur een mail aan: <a href="mailto:support@ploko.nl" style={{ color: '#E8711A', fontWeight: 600 }}>support@ploko.nl</a> dan overleggen we.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link className="btn btn-primary" to="/instellingen">Instellingen</Link>
              {user?.role === 'admin' && <Link className="btn btn-secondary" to="/instellingen/admin">Admin</Link>}
            </div>
          </div>
        )}

        {showSetupBlock ? (
          <Routes>
            <Route path="/instellingen" element={<Settings asPage />} />
            <Route
              path="/instellingen/admin"
              element={user?.role === 'admin' ? <AdminSettings /> : <Navigate to="/instellingen" replace />}
            />
            <Route
              path="/instellingen/admin/cms"
              element={user?.role === 'admin' ? <CmsAdmin /> : <Navigate to="/instellingen" replace />}
            />
            <Route path="*" element={null} />
          </Routes>
        ) : (
          <Routes>
            <Route path="/" element={<QuotationWizard key={wizardKey} />} />
            <Route path="/offertes" element={<SavedQuotations />} />
            <Route path="/aanvragen" element={<AanvragenDashboard />} />
            <Route path="/instellingen" element={<Settings asPage />} />
            <Route
              path="/instellingen/admin"
              element={user?.role === 'admin' ? <AdminSettings /> : <Navigate to="/" replace />}
            />
            <Route
              path="/instellingen/admin/cms"
              element={user?.role === 'admin' ? <CmsAdmin /> : <Navigate to="/" replace />}
            />
          </Routes>
        )}
      </div>
    </AppErrorBoundary>
  );
}

function AppContent() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="App" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div>Laden...</div>
      </div>
    );
  }

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ScrollToTop />
      <Routes>
        <Route path="/welkom" element={<LandingPage />} />
        <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
        <Route path="/demo" element={<DemoLayout />} />
        <Route path="/blog" element={<BlogList />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/pagina/:slug" element={<PageView />} />
        <Route path="/*" element={!user ? <LandingPage /> : <ProtectedLayout />} />
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
